import { CountryUtilService } from '@shared/services/country-util/country-util.service';
import { environment } from '@env';
import { Observable, of } from 'rxjs';
import { Injectable } from '@angular/core';
import { TableUser, User } from '@core/modules/user/models/user';
import { Profession } from '@core/models/profession';
import { UserApiService } from './user-api.service';
import { map, shareReplay, withLatestFrom } from 'rxjs/operators';
import { Customer, UserInterest } from '../models';
import { Store } from '@ngrx/store';
import { selectUser } from '../features/user.slice';
import { DeviceUser } from '@core/models/device-user';
import { PromoteType } from '@shared/enums/promote-type.enum';
import { selectCurrentLanguages } from '@core/modules/language/features/language.slice';
import { LanguageService } from '@core/modules/language/services/language.service';
import { Locale } from '@core/models/lang';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  API_URL = environment.api;

  constructor(
    private userApiService: UserApiService,
    private store: Store,
    private countryUtil: CountryUtilService,
    private readonly languageService: LanguageService
  ) {}

  getUserInterests(userId: number | undefined): Observable<UserInterest[]> {
    return this.userApiService.fetchUserInterests(userId?.toString() || '');
  }

  getUser(email: string): Observable<User> {
    return this.userApiService.fetchUser(email);
  }

  completeUserProfile(user: User): Observable<User> {
    return this.userApiService.completeUserProfile(user);
  }

  updateUser(user: User): Observable<User> {
    return this.userApiService.updateUser(user);
  }

  addSubscription(userId: User['id'], promoteType: PromoteType) {
    return this.userApiService.addSubscription(userId, promoteType);
  }

  removeSubscription(userId: User['id'], promoteType: PromoteType) {
    return this.userApiService.removeSubscription(userId, promoteType);
  }

  getPublicContent(): Observable<any> {
    return of(null);
  }

  getUserBoard(): Observable<any> {
    return of(null);
  }

  getModeratorBoard(): Observable<any> {
    return of(null);
  }

  getAdminBoard(): Observable<any> {
    return of(null);
  }

  getUsers(): Observable<{ count: number }> {
    return this.userApiService.fetchCustomersCount();
  }

  getDevicesBySerials(serials: string[]): Observable<DeviceUser[]> {
    return this.userApiService.fetchDevicesBySerials(serials);
  }

  getProfessionsBySerial(serial: string): Observable<Profession[]> {
    return this.userApiService.fetchProfessionsBySerial(serial);
  }

  getProfessionsByUser(userId: number): Observable<Profession[]> {
    return this.userApiService.fetchProfessionsByUser(userId);
  }

  deleteAccount(userId: number): Observable<void> {
    return this.userApiService.deleteAccount(userId);
  }

  deleteDeviceBySerial({
    userId,
    device
  }: {
    userId: number;
    device: DeviceUser;
  }): Observable<any> {
    return this.userApiService.deleteUserDeviceBySerial(userId, device);
  }

  parseToDataTable(users: Customer[]): TableUser[] {
    users = this.sortByCreationDate(users);
    return users
      .filter((user) => user.serials)
      .map((user) => ({
        username: user.email,
        devices: !user.serials?.length
          ? ''
          : user?.serials[user?.serials?.length - 1]?.device_model?.device?.code ||
            '' + ' (' + user?.serials[user?.serials?.length - 1]?.serial + ')'
      }));
  }

  sortByCreationDate(users: Customer[]) {
    users = users.sort((a: Customer, b: Customer) => {
      return +new Date(b.createdAt) - +new Date(a.createdAt);
    });
    return users;
  }

  isSpanishUser(): Observable<boolean> {
    return this.store.select(selectUser).pipe(
      map((user) => this.countryUtil.isSpain(user?.countryId)),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  isUSAUser(): Observable<boolean> {
    return this.store.select(selectUser).pipe(
      map((user) => this.countryUtil.isUSA(user?.countryId)),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  isLang(lang: Locale): Observable<boolean> {
    return this.store.select(selectUser).pipe(
      withLatestFrom(this.store.select(selectCurrentLanguages)),
      map(([user, languages]) => {
        if (!user) return false;

        const userLang = this.languageService.getLanguageLocaleById(user.langId, languages);

        return userLang.toLowerCase() === lang.toLowerCase();
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  canShowShopProps(): Observable<{ isInWhiteList: boolean; isSpanishUser: boolean }> {
    const emails = (environment.showShopWhiteList ?? []).map((email) => email.toLowerCase());

    return this.store.select(selectUser).pipe(
      map((user) => ({
        isInWhiteList: emails.includes(user?.email.toLowerCase() ?? ''),
        isSpanishUser: this.countryUtil.isSpain(user?.countryId)
      })),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }
}
